import { useEffect, useState, type MouseEvent } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Icon } from "../icons/Icon.tsx";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { tierMeets, type TierGate } from "../../auth/tier-gates.ts";
import { PRODUCT_NAV_GROUPS, productsByNavGroup, type ProductFormat, type ProductNavGroup } from "../../pages/tennis/products/registry.ts";
import { useActiveSport } from "../../sports/useActiveSport.ts";
import { PERSONAS, PERSONA_ORDER } from "../../pages/basketball/big6Registry.ts";
import { useBig6 } from "../../pages/basketball/useBasketballProducts.ts";

// Human label for the live tier code returned by user_active_tier().
// `black` is the founder/top rank (see auth/tier-gates.ts RANK).
const TIER_DISPLAY: Record<string, string> = {
  black: "Black",
  desk_pro: "Desk Pro",
  elite: "Desk Pro",
  phantom_pro: "Phantom Pro",
  pro: "Phantom Pro",
  sika_pro: "Sika Pro",
  free: "Free",
};

interface NavItem {
  id: string;
  label: string;
  icon: string;
  to?: string;
  badge?: { label?: string; live?: boolean; dot?: boolean };
  children?: NavItem[];
  /** Tier gate — when set and the user's tier is below it, the item
   *  shows a lock and routes to /pricing (the upgrade prompt). */
  gate?: TierGate;
}

// SIKA parent expansion persistence — gap #13 in SIKA-5b.
const SIKA_EXPANDED_KEY = "strikza:sidebar:sika:expanded";

function readSikaExpanded(): boolean {
  try {
    const v = typeof localStorage !== "undefined" ? localStorage.getItem(SIKA_EXPANDED_KEY) : null;
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
  } catch {
    /* SSR / blocked */
  }
  return true; // default open
}

function writeSikaExpanded(v: boolean): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(SIKA_EXPANDED_KEY, v ? "1" : "0");
    }
  } catch {
    /* ignore */
  }
}

// Free shop window — public/anon surfaces. Daily Acca lives here.
// Super Acca (paid Bet Builder) joins this group as Phase D lands.
const NAV_FREE: NavItem[] = [
  { id: "daily-acca", label: "Daily Acca", icon: "exchange", to: "/daily-acca" },
  { id: "super-acca", label: "Super Acca", icon: "exchange", to: "/super-acca" },
];

// Football home/overview — the Home + Dashboard pages render football activity
// (chains, qualified signals, ROI), so they belong to the Football workspace,
// NOT the global chrome. Tennis lands on its own Signals page instead.
const NAV_FOOTBALL_HOME: NavItem[] = [
  { id: "home", label: "Home", icon: "home", to: "/home" },
  { id: "dashboard", label: "Dashboard", icon: "home", to: "/dashboard" },
];

// GLOBAL markets & transparency — genuinely cross-sport surfaces. The Exchange
// stays global per founder ruling; Coverage + Markets are multi-sport honest
// transparency surfaces.
const NAV_WORKSPACE: NavItem[] = [
  {
    id: "exchange",
    label: "The Exchange",
    icon: "exchange",
    to: "/exchange",
    badge: { label: "12", live: true },
  },
  { id: "boards", label: "The Board", icon: "exchange", to: "/boards" },
  // Coverage & Roadmap — honest transparency surface over the sports
  // registry + 4-condition gate (anon-readable, all tiers).
  { id: "coverage", label: "Coverage", icon: "intel", to: "/coverage" },
  // Markets transparency — modelled vs explicitly-declined (with
  // reasons). Same honest-moat philosophy as Coverage.
  { id: "markets", label: "Markets", icon: "intel", to: "/markets" },
];

const NAV_DAILY: NavItem[] = [
  {
    id: "singles",
    label: "Singles Chain",
    icon: "singles",
    to: "/singles",
    badge: { label: "D3" },
  },
  { id: "rollover", label: "ROLLOVER", icon: "rollover", to: "/rollover", badge: { dot: true } },
  { id: "streaks", label: "STREAKS", icon: "rollover", to: "/streaks" },
];

const NAV_WEEKLY: NavItem[] = [
  {
    id: "powerball",
    label: "POWERBALL",
    icon: "powerball",
    to: "/powerball",
    badge: { label: "Daily", live: true },
  },
  { id: "under-dogs", label: "UNDER DOGS", icon: "powerball", to: "/under-dogs", badge: { label: "Daily" } },
  { id: "form-elite", label: "FORM ELITE", icon: "intel", to: "/form-elite" },
  { id: "derivatives", label: "DERIVATIVES", icon: "exchange", to: "/derivatives", badge: { label: "New" } },
  { id: "goals-galore", label: "Goals Galore", icon: "exchange", to: "/goals-galore", badge: { label: "New" } },
  { id: "ladder", label: "LADDER", icon: "ladder", to: "/ladder" },
];

const NAV_LATE_NIGHT: NavItem[] = [
  { id: "owl", label: "OWL HOUR", icon: "owl", to: "/owl", badge: { label: "4 Tiers", live: true } },
];

// Leagues — per-league weekly intelligence. Each league is a pure selector over
// the Weekly Substrate (fair price + Desk verdict + CLV), gated Pro+. Pilot
// ships the English Premier League; further leagues slot in as their own
// children with the SAME route shape (/leagues/<slug>).
const NAV_LEAGUES: NavItem[] = [
  {
    id: "leagues",
    label: "Leagues",
    icon: "intel",
    to: "/leagues/epl",
    gate: "phantom_pro",
    children: [
      { id: "league-epl", label: "English Premier League", icon: "intel", to: "/leagues/epl", gate: "phantom_pro" },
    ],
  },
];

// Football Intelligence — the football workspace's signal surfaces. Shown only
// when the active sport is Football (the sport switcher swaps this out).
const NAV_INTEL: NavItem[] = [
  { id: "desk", label: "The Desk", icon: "intel", to: "/desk", badge: { live: true } },
  { id: "signals", label: "Signal Window", icon: "exchange", to: "/signals" },
  { id: "fairprice", label: "Fair Price", icon: "intel", to: "/fair-price" },
  { id: "highconf", label: "High Confidence", icon: "intel", to: "/high-confidence", badge: { live: true } },
  { id: "clv", label: "Line Movement", icon: "exchange", to: "/clv" },
  {
    id: "sika",
    label: "SIKA",
    icon: "sika",
    to: "/sika",
    children: [
      { id: "sika-chat", label: "SIKA Chat", icon: "sika", to: "/sika/chat" },
    ],
  },
  // International Acca + all its sub-routes (Hub, Calendar, DNA, Debrief,
  // Ladder, Pass, Archive, Qualifier) have moved out of the Football sidebar
  // into the dedicated International workspace (Sport Workspaces v1.1).
  // The routes still exist under /intl-acca/* and are claimed by the
  // International workspace's matchPrefixes — users reach them via the
  // hero sport switcher (Football → International) and the International
  // sidebar's Products group. Removed from here to avoid duplication.
];

// Bankroll & Vault — GLOBAL across every sport (founder ruling). Your capital,
// vault and watchlist are one bankroll, not per-sport, so these never swap.
const NAV_VAULT: NavItem[] = [
  { id: "bankroll", label: "Bankroll", icon: "vault", to: "/bankroll" },
  { id: "vault", label: "Vault", icon: "vault", to: "/vault" },
  { id: "watchlist", label: "Watchlist", icon: "watch", to: "/watchlist", badge: { label: "1" } },
];

// ── Tennis workspace ─────────────────────────────────────────────
// Tennis is its own sport workspace (selected via the hero sport switcher). Its
// nav is split into labelled sub-sections — Overview, the product groups
// (Daily Plays / Jackpot & Accas / Live & Tracked / Intelligence Layers, driven
// by the registry's navGroup), then Analysis — so it never reads as one long
// flat list. Each product is its OWN entry → its OWN page.
// Per-format sidebar icon (variety, not one repeated).
const TENNIS_FORMAT_ICON: Record<ProductFormat, string> = {
  acca: "exchange",
  board: "reports",
  card: "powerball",
  tiers: "ladder",
  single: "singles",
  alert: "owl",
};

function tennisProductNav(group: ProductNavGroup): NavItem[] {
  return productsByNavGroup(group).map((p) => ({
    id: `tennis-p-${p.slug}`,
    label: p.name,
    icon: TENNIS_FORMAT_ICON[p.format] ?? "powerball",
    to: `/exchange/tennis/p/${p.slug}`,
    gate: p.tier,
  }));
}

const NAV_TENNIS_OVERVIEW: NavItem[] = [
  { id: "tennis-dashboard", label: "Dashboard", icon: "home", to: "/exchange/tennis/dashboard" },
  { id: "tennis-signals", label: "Signals", icon: "exchange", to: "/exchange/tennis/signals" },
];

const NAV_TENNIS_ANALYSIS: NavItem[] = [
  { id: "tennis-tournament", label: "Tournament", icon: "ladder", to: "/exchange/tennis/tournament" },
  { id: "tennis-clv", label: "CLV Track", icon: "intel", to: "/exchange/tennis/clv", gate: "phantom_pro" },
  { id: "tennis-nobets", label: "No-Bet Report", icon: "reports", to: "/exchange/tennis/no-bets" },
  { id: "tennis-markets", label: "Markets", icon: "intel", to: "/exchange/tennis/markets", gate: "desk_pro" },
];

// ── Basketball workspace (Phase 1a) ──────────────────────────────
// Dormant pilot: its own sidebar group so the sport separation is locked in,
// but every entry resolves to the honest "in calibration" landing until the
// BFPE + agents ship and the calibration/CLV gate opens. No live products yet.
const NAV_BASKETBALL_OVERVIEW: NavItem[] = [
  { id: "bball-dashboard", label: "Dashboard", icon: "home", to: "/exchange/basketball" },
  { id: "bball-slate", label: "Today's Slate", icon: "exchange", to: "/exchange/basketball/slate" },
  { id: "bball-signals", label: "Signals", icon: "target", to: "/exchange/basketball/signals" },
  { id: "bball-singles", label: "BBALL SINGLES", icon: "target", to: "/exchange/basketball/singles" },
];

// Acca Products (Basketball_Acca_Products_v1.0) — each its own screen + BFPE
// Intelligence Drawer. Dormant pilot: navigable but honest-empty until the
// calibration gate opens (sportsRegistry stays 'planned'). Prop Pulse (P05) +
// Overtime Trap (P07) live as tabs inside Signals, not top-level (per doc).
const NAV_BASKETBALL_PRODUCTS: NavItem[] = [
  { id: "bball-first-half", label: "First Half Intelligence", icon: "exchange", to: "/exchange/basketball/first-half" },
  { id: "bball-night-shift", label: "The Night Shift", icon: "intel", to: "/exchange/basketball/night-shift" },
  { id: "bball-q1-strike", label: "Q1 Strike", icon: "target", to: "/exchange/basketball/q1-strike" },
  { id: "bball-hardwood-slip", label: "Hardwood Slip", icon: "ladder", to: "/exchange/basketball/hardwood-slip" },
  { id: "bball-powerball", label: "POWERBALL", icon: "vault", to: "/exchange/basketball/powerball" },
  { id: "bball-spread-fade", label: "Spread Fade", icon: "trend", to: "/exchange/basketball/spread-fade" },
  { id: "bball-gauntlet", label: "The Hardwood Gauntlet", icon: "flame", to: "/exchange/basketball/gauntlet" },
];

const NAV_BASKETBALL_ANALYSIS: NavItem[] = [
  { id: "bball-clv", label: "CLV Track", icon: "trend", to: "/exchange/basketball/clv", gate: "phantom_pro" },
  { id: "bball-nobets", label: "No-Bet Report", icon: "reports", to: "/exchange/basketball/no-bets" },
  { id: "bball-leagues", label: "Leagues", icon: "intel", to: "/exchange/basketball/leagues" },
];

// ── International workspace (Sport Workspaces v1.1) ──────────────────────
// The international football surface: UCL / UEL / Conference / Euro qual /
// WC 2026 / futures. Modelled as a 4th sport-switcher sibling (NOT a new
// sport — underlying engine, substrate and slip-moderator are still football).
// Sidebar deep-links into BOTH the new /exchange/international/* surfaces AND
// the established /intl-acca/* route tree (claimed by the workspace registry).
const NAV_INTERNATIONAL_OVERVIEW: NavItem[] = [
  { id: "intl-dashboard", label: "Dashboard", icon: "home", to: "/exchange/international" },
  { id: "intl-signals", label: "Signals", icon: "target", to: "/exchange/international/signals" },
  { id: "intl-wc", label: "WC 2026 Hub", icon: "international", to: "/exchange/international/wc-2026" },
  { id: "intl-rounds", label: "Rounds", icon: "ladder", to: "/exchange/international/rounds" },
];

const NAV_INTERNATIONAL_PRODUCTS: NavItem[] = [
  { id: "intl-btb", label: "Break The Bank", icon: "vault", to: "/exchange/international/break-the-bank", badge: { label: "21 agents" } },
  { id: "intl-friendlies", label: "Friendlies", icon: "flag", to: "/exchange/international/friendlies" },
  { id: "intl-acca", label: "International Acca", icon: "ladder", to: "/intl-acca/hub" },
  { id: "intl-ladder", label: "LADDER-Intl", icon: "ladder", to: "/intl-acca/ladder" },
  { id: "intl-rolls", label: "Rolls", icon: "trend", to: "/intl-acca/rolls" },
  { id: "intl-bet-builder", label: "Bet Builder", icon: "exchange", to: "/intl-acca/bet-builder" },
  { id: "intl-calendar", label: "Tournaments", icon: "knowledge", to: "/intl-acca/calendar" },
  { id: "intl-pass", label: "Tournament Pass", icon: "vault", to: "/intl-acca/pass" },
  { id: "intl-archive", label: "Slip Archive", icon: "reports", to: "/intl-acca/archive" },
];

const NAV_INTERNATIONAL_ANALYSIS: NavItem[] = [
  { id: "intl-clv", label: "CLV Track", icon: "trend", to: "/exchange/international/clv", gate: "phantom_pro" },
  { id: "intl-nobets", label: "No-Bet Report", icon: "reports", to: "/exchange/international/no-bets" },
  { id: "intl-debrief", label: "Debrief", icon: "audit", to: "/intl-acca/debrief" },
  { id: "intl-dna", label: "DNA", icon: "intel", to: "/intl-acca/dna" },
];

// Lab — the user's bench. Surfaces where the user brings their own
// slip (or future input) and the system reasons about it. Distinct
// from Intelligence above (where the user consumes STRIKZA's signals)
// and Tools below (operator + admin chrome). Future Lab residents:
// Slip Builder, Slip Simulator, Bankroll Sim, Pattern Lab.
// Slip Intelligence suite lives entirely under Lab — pipeline order:
// Extractor (read) → Repair (rebuild) → Autopsy (learn). Autopsy
// route is the placeholder /repair/autopsy stub until the full
// Autopsy view ships; Lab-level Slip Autopsy at /slips/autopsy is
// the existing experience and stays where it is for now.
const NAV_LAB: NavItem[] = [
  { id: "extractor", label: "Slip Extractor", icon: "funnel", to: "/slips/extractor" },
  { id: "swarm", label: "Slip Analysis", icon: "bolt", to: "/swarm", badge: { label: "20 agents" } },
  { id: "repair", label: "Slip Repair", icon: "audit", to: "/repair" },
  { id: "autopsy", label: "Slip Autopsy", icon: "audit", to: "/slips/autopsy" },
];

const NAV_TOOLS: NavItem[] = [
  { id: "intelligence", label: "Intelligence", icon: "intel", to: "/intelligence" },
  { id: "agents", label: "Agents", icon: "audit", to: "/agents" },
  { id: "templates", label: "Templates", icon: "knowledge", to: "/templates" },
  { id: "reports", label: "Reports", icon: "reports", to: "/reports" },
  { id: "audit", label: "Audit log", icon: "audit", to: "/audit" },
  { id: "knowledge", label: "Knowledge", icon: "knowledge", to: "/knowledge" },
  { id: "academy", label: "Academy", icon: "knowledge", to: "/academy" },
  { id: "trust", label: "Trust Center", icon: "intel", to: "/trust" },
  { id: "admin", label: "Admin", icon: "audit", to: "/admin" },
  { id: "cost", label: "Cost", icon: "audit", to: "/admin/cost" },
  { id: "activation", label: "Activation", icon: "audit", to: "/admin/activation" },
  { id: "odds-health", label: "Odds Health", icon: "audit", to: "/admin/odds-health" },
  { id: "display", label: "Display", icon: "knowledge", to: "/settings/display" },
];

// Collapsible section header. Used for the Free Tier group so the
// "shop window" entries (Daily Acca, future Super Acca) can be folded
// out of view when the operator is focused on paid surfaces.
function CollapsibleSection({
  label,
  items,
  defaultOpen = true,
}: {
  label: string;
  items: NavItem[];
  defaultOpen?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  return (
    <>
      <button
        type="button"
        className={`sb-section sb-section-toggle${open ? " open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{label}</span>
        <Icon name="chev" size={11} />
      </button>
      {open ? <NavGroup items={items} /> : null}
    </>
  );
}

function renderBadge(badge: NonNullable<NavItem["badge"]>): JSX.Element {
  if (badge.dot) return <span className="badge dot" />;
  return <span className={`badge${badge.live ? " live" : ""}`}>{badge.label}</span>;
}

function NavLeaf({
  item,
  isChild = false,
  tierCode,
}: {
  item: NavItem;
  isChild?: boolean;
  tierCode?: string | null;
}): JSX.Element {
  const location = useLocation();
  // Tier gate: if the item declares a gate the user doesn't meet, lock it —
  // show a lock and route to /pricing (the upgrade prompt) instead.
  const locked = item.gate ? !tierMeets(tierCode ?? null, item.gate) : false;
  const dest = item.to ?? "#";
  const to = locked ? "/pricing" : dest;
  const isActive = !locked && (location.pathname === dest || location.pathname.startsWith(dest + "/"));
  const cls = `sb-item${isChild ? " sb-item-child" : ""}${isActive ? " active" : ""}${locked ? " locked" : ""}`;
  return (
    <NavLink to={to} className={cls} title={locked ? "Upgrade to unlock" : undefined}>
      <Icon name={item.icon} />
      <span>{item.label}</span>
      {locked ? <Icon name="lock" size={12} className="lock-icon" /> : item.badge && renderBadge(item.badge)}
    </NavLink>
  );
}

function NavParent({ item, tierCode }: { item: NavItem; tierCode?: string | null }): JSX.Element {
  const location = useLocation();
  const children = item.children ?? [];
  // Auto-expand when any descendant route is active. localStorage seed otherwise.
  const routeMatch =
    item.to !== undefined &&
    (location.pathname === item.to || location.pathname.startsWith(item.to + "/"));
  const [open, setOpen] = useState<boolean>(() => routeMatch || readSikaExpanded());

  useEffect(() => {
    if (routeMatch && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMatch]);

  const groupActive =
    routeMatch ||
    children.some(
      (c) =>
        c.to !== undefined &&
        (location.pathname === c.to || location.pathname.startsWith(c.to + "/")),
    );
  const parentCls = `sb-item sb-item-parent${groupActive ? " active" : ""}`;

  const toggle = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => {
      const next = !v;
      writeSikaExpanded(next);
      return next;
    });
  };

  const labelNode =
    item.to !== undefined ? (
      <div className="sb-parent-row">
        <NavLink to={item.to} className={parentCls} end={false}>
          <Icon name={item.icon} />
          <span>{item.label}</span>
        </NavLink>
        <button
          type="button"
          className={`sb-chev${open ? " open" : ""}`}
          onClick={toggle}
          aria-expanded={open}
          aria-controls={`sb-children-${item.id}`}
          aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
        >
          <Icon name="chev" size={11} />
        </button>
      </div>
    ) : (
      <button type="button" className={parentCls} onClick={toggle} aria-expanded={open}>
        <Icon name={item.icon} />
        <span>{item.label}</span>
        <span className={`sb-chev${open ? " open" : ""}`}>
          <Icon name="chev" size={11} />
        </span>
      </button>
    );

  return (
    <div className="sb-group" data-group={item.id} data-open={open}>
      {labelNode}
      {open ? (
        <div className="sb-children" id={`sb-children-${item.id}`}>
          {children.map((c) => (
            <NavLeaf key={c.id} item={c} isChild={true} tierCode={tierCode} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NavGroup({ items, tierCode }: { items: NavItem[]; tierCode?: string | null }): JSX.Element {
  return (
    <nav className="sb-nav">
      {items.map((n) =>
        n.children && n.children.length > 0 ? (
          <NavParent key={n.id} item={n} tierCode={tierCode} />
        ) : (
          <NavLeaf key={n.id} item={n} tierCode={tierCode} />
        ),
      )}
    </nav>
  );
}

// THE BIG 6 — collapsible persona component (ported from the CLAUDE DESIGN
// prototype BbSidebar). Star header with a live total leg count + "6
// professionals · 60 daily legs" subtext; expands to six accent-striped persona
// sub-items with their own live counts. Uses the basketball-specific sidebar
// classes (sb-bb-collapse, sb-sub, sb-sub-item, pdot, ct) — not the shared
// NavParent. Only mounts inside the basketball sidebar, so its read hook runs
// only there. Archetype labels only (real-professional lineage internal).
function BigSixNav(): JSX.Element {
  const location = useLocation();
  const { data } = useBig6();
  const routeMatch = location.pathname.startsWith("/exchange/basketball/big6");
  const [open, setOpen] = useState<boolean>(() => routeMatch || true);
  useEffect(() => {
    if (routeMatch && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMatch]);
  const total = data?.total_legs ?? 0;
  return (
    <>
      <button
        type="button"
        className={`sb-bb-collapse${open ? " open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon name="star" size={15} />
        <div className="ttl">
          <div>THE BIG 6</div>
          <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", letterSpacing: "0.08em", fontFamily: "var(--font-mono)" }}>
            6 professionals · 60 daily legs
          </div>
        </div>
        <span className="ct">{total}</span>
        <Icon name="chev" size={13} />
      </button>
      {open ? (
        <div className="sb-sub">
          {PERSONA_ORDER.map((slug) => {
            const persona = PERSONAS[slug];
            const dest = `/exchange/basketball/big6/${slug}`;
            const count = data?.personas?.[slug]?.count ?? 0;
            return (
              <NavLink
                key={slug}
                to={dest}
                className={({ isActive }) => `sb-sub-item${isActive ? " active" : ""}`}
                style={{ ["--ppc" as string]: persona.accent }}
                title={persona.market}
              >
                <span className="pdot" />
                <span>{persona.name}</span>
                <span className="ct">{count}</span>
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

export function Sidebar(): JSX.Element {
  const { user } = useAuthSession();
  const { tierCode, loading: tierLoading } = useUserTier();
  const { active } = useActiveSport();
  const initials = user?.email ? user.email.substring(0, 2).toUpperCase() : "KA";
  const displayName = user?.email ? user.email.split("@")[0] : "User";
  const tierLabel = tierLoading
    ? "…"
    : (tierCode && TIER_DISPLAY[tierCode]) || "Free";

  return (
    <aside className="sidebar">
      <div className="sb-brand">
        <div className="logo">S</div>
        <div className="name">Strikza</div>
        <div className="ws">Phase 1A</div>
      </div>

      <div className="sb-search">
        <Icon name="search" size={14} />
        <span>Search…</span>
        <span className="kbd">⌘K</span>
      </div>

      <div className="sb-scroll">
        {/* PER-SPORT — the active sport's own workspace, at the top. Swaps with
            the hero sport switcher; each sport shows ONLY its own data. */}
        {active.key === "football" ? (
          <>
            <div className="sb-section">Workspace</div>
            <NavGroup items={NAV_FOOTBALL_HOME} />

            <CollapsibleSection label="Free shop window" items={NAV_FREE} defaultOpen={true} />

            <div className="sb-section">Daily</div>
            <NavGroup items={NAV_DAILY} />

            <div className="sb-section">Weekly</div>
            <NavGroup items={NAV_WEEKLY} />

            <div className="sb-section">Late Night</div>
            <NavGroup items={NAV_LATE_NIGHT} />

            <div className="sb-section">Intelligence</div>
            <NavGroup items={NAV_INTEL} />

            <div className="sb-section">Leagues</div>
            <NavGroup items={NAV_LEAGUES} tierCode={tierCode} />
          </>
        ) : null}

        {active.key === "tennis" ? (
          <>
            <div className="sb-section">Tennis</div>
            <NavGroup items={NAV_TENNIS_OVERVIEW} />

            {PRODUCT_NAV_GROUPS.map(({ group, label }) => (
              <div key={group}>
                <div className="sb-section">{label}</div>
                <NavGroup items={tennisProductNav(group)} tierCode={tierCode} />
              </div>
            ))}

            <div className="sb-section">Analysis</div>
            <NavGroup items={NAV_TENNIS_ANALYSIS} tierCode={tierCode} />
          </>
        ) : null}

        {active.key === "basketball" ? (
          <>
            <div className="sb-section">Basketball</div>
            <NavGroup items={NAV_BASKETBALL_OVERVIEW} />

            <div className="sb-section">Products</div>
            <NavGroup items={NAV_BASKETBALL_PRODUCTS} tierCode={tierCode} />

            <div className="sb-section">The Big 6</div>
            <BigSixNav />

            <div className="sb-section">Analysis</div>
            <NavGroup items={NAV_BASKETBALL_ANALYSIS} tierCode={tierCode} />
            {/* Calibration-dormant footer cap removed 2026-05-26 once
                basketball workspace was flipped to live (founder pilot;
                public launch 2027). The status pulse in BbHead is now the
                source of truth for the workspace's live-state badge. */}
          </>
        ) : null}

        {active.key === "international" ? (
          <>
            <div className="sb-section">International</div>
            <NavGroup items={NAV_INTERNATIONAL_OVERVIEW} />

            <div className="sb-section">Products</div>
            <NavGroup items={NAV_INTERNATIONAL_PRODUCTS} tierCode={tierCode} />

            <div className="sb-section">Analysis</div>
            <NavGroup items={NAV_INTERNATIONAL_ANALYSIS} tierCode={tierCode} />
          </>
        ) : null}

        {/* GLOBAL betting surfaces (Markets, Bankroll & Vault, Lab, Tools) live
            ONLY in the Football workspace — Football is the default betting
            system. Founder ruling: keep Tennis & Basketball sidebars focused on
            their own sport; users reach these via the Football workspace (one
            topbar switch away). This avoids cluttering the sport modules. */}
        {active.key === "football" ? (
          <>
            <div className="sb-section">Markets</div>
            <NavGroup items={NAV_WORKSPACE} />

            <div className="sb-section">Bankroll &amp; Vault</div>
            <NavGroup items={NAV_VAULT} />

            <div className="sb-section">Lab</div>
            <NavGroup items={NAV_LAB} />

            <div className="sb-section">Tools</div>
            <NavGroup items={NAV_TOOLS} />
          </>
        ) : null}
      </div>

      <div className="sb-foot">
        <div className="sb-user">
          <div className="avatar">{initials}</div>
          <div className="meta">
            <div>{displayName}</div>
            <div className="tier">{tierLabel} · GH</div>
          </div>
          <Icon name="chev" size={14} />
        </div>
      </div>
    </aside>
  );
}
